
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Filter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CategoryFilter } from "./CategoryFilter";
import { SystemAppFilter } from "./SystemAppFilter";
import { TagsFilter } from "./TagsFilter";

export interface FilterState {
  category: string;
  systemAppFilter: 'all' | 'system' | 'non-system';
  tags: string[];
}

interface AppFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onClearFilters: () => void;
}

export const AppFilters = ({ filters, onFiltersChange, onClearFilters }: AppFiltersProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasActiveFilters = filters.category !== 'all' || 
                          filters.systemAppFilter !== 'all' || 
                          filters.tags.length > 0;

  const handleCategoryChange = (category: string) => {
    onFiltersChange({ ...filters, category });
  };

  const handleSystemAppFilterChange = (systemAppFilter: 'all' | 'system' | 'non-system') => {
    onFiltersChange({ ...filters, systemAppFilter });
  };

  const handleTagsChange = (tags: string[]) => {
    onFiltersChange({ ...filters, tags });
  };

  const removeFilter = (type: 'category' | 'systemAppFilter' | 'tag', value?: string) => {
    if (type === 'category') {
      onFiltersChange({ ...filters, category: 'all' });
    } else if (type === 'systemAppFilter') {
      onFiltersChange({ ...filters, systemAppFilter: 'all' });
    } else if (type === 'tag' && value) {
      onFiltersChange({ ...filters, tags: filters.tags.filter(tag => tag !== value) });
    }
  };

  const getSystemAppFilterLabel = (filter: string) => {
    switch (filter) {
      case 'system': return 'System Apps';
      case 'non-system': return 'User Apps';
      default: return '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filter
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {(filters.category !== 'all' ? 1 : 0) + 
                   (filters.systemAppFilter !== 'all' ? 1 : 0) + 
                   filters.tags.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="start">
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-sm mb-2">Category</h4>
                <CategoryFilter 
                  selectedCategory={filters.category}
                  onCategoryChange={handleCategoryChange}
                />
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-2">App Type</h4>
                <SystemAppFilter 
                  value={filters.systemAppFilter}
                  onValueChange={handleSystemAppFilterChange}
                />
              </div>

              <div>
                <h4 className="font-medium text-sm mb-2">Tags</h4>
                <TagsFilter 
                  selectedTags={filters.tags}
                  onTagsChange={handleTagsChange}
                />
              </div>

              {hasActiveFilters && (
                <div className="pt-2 border-t">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      onClearFilters();
                      setIsOpen(false);
                    }}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.category !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Category: {filters.category}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                onClick={() => removeFilter('category')}
              />
            </Badge>
          )}
          
          {filters.systemAppFilter !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              {getSystemAppFilterLabel(filters.systemAppFilter)}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                onClick={() => removeFilter('systemAppFilter')}
              />
            </Badge>
          )}
          
          {filters.tags.map(tag => (
            <Badge key={tag} variant="secondary" className="flex items-center gap-1">
              Tag: {tag}
              <X 
                className="w-3 h-3 cursor-pointer hover:text-red-500" 
                onClick={() => removeFilter('tag', tag)}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
